import { Fragment, useContext, useMemo, useState } from 'react';

import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faTooth } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { selectTeethAtom } from '@/modules/patientList/atoms';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import type { ImageObject } from '../types';

import { getTeethFilteredByImageView, getVisitSidebarView } from './helpers';
import { ToothDamageProgress } from './ToothDamageProgress';

const TOOTH_DAMAGE_ITEMS_LIMIT = 8;
export const MIN_DMF_THRESHOLD = 0.15;

interface Props {
  activeImage?: ImageObject;
  estimatedVisitId?: number | null;
  isLoading: boolean;
  teeth: TVisitSummary['teeth'];
  unifiedAppointmentId?: number;
}

export const ToothDamage = ({
  activeImage,
  estimatedVisitId,
  isLoading,
  teeth,
  unifiedAppointmentId,
}: Props) => {
  const [showMore, setShowMore] = useState(false);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const { trackEvent } = useTrackEvent();
  const { displayToothNumber } = useUniversalToFDI();
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();

  const teethFilteredByImageView = getTeethFilteredByImageView(
    teeth,
    activeImage,
    selectedTeeth,
  );

  // Filter out teeth with undefined or 0% DMF, and sort descending by DMF
  const teethFilteredAndSorted = useMemo(() => {
    // Checking for truthiness instead of a number because we don't want to show
    // DMF of 0%
    const teethFiltered = teethFilteredByImageView?.filter(({ dmf }) => !!dmf);
    return teethFiltered?.sort((a, b) => b.dmf! - a.dmf!);
  }, [teethFilteredByImageView]);

  const handleMeasurementsToggle = () => {
    const isNewShowMeasurements = !showMore;
    trackEvent(mixpanelEvents.visitSidebarToothDamageToggle, {
      isOpen: isNewShowMeasurements,
      estimatedVisitId,
      unifiedAppointmentId,
      view: getVisitSidebarView(activeImage, selectedTeeth),
      isIframe: !!isIframe,
      externalPatientId,
      externalImageId,
      patientId: selectedPatientId ?? patientId,
      imageId: activeImage?.imageId,
      trialStatus,
      centralizedBilling: isCentralizedBilling,
    });
    setShowMore(isNewShowMeasurements);
  };

  const teethWithSignificantDmf = teethFilteredAndSorted.filter(
    ({ dmf }) => dmf && dmf >= MIN_DMF_THRESHOLD,
  );
  const displayTeeth = useMemo(() => {
    if (showMore) return teethFilteredAndSorted;

    return teethWithSignificantDmf.length
      ? teethWithSignificantDmf.slice(0, TOOTH_DAMAGE_ITEMS_LIMIT)
      : [];
  }, [showMore, teethFilteredAndSorted, teethWithSignificantDmf]);

  const hasDmfMeasurements = !!teethFilteredAndSorted.length;
  const hideToggle =
    teethFilteredAndSorted.every(
      ({ dmf }) => dmf && dmf >= MIN_DMF_THRESHOLD,
    ) && teethFilteredAndSorted.length <= TOOTH_DAMAGE_ITEMS_LIMIT;
  const showNoSignificantDmfMessage =
    hasDmfMeasurements && !showMore && !teethWithSignificantDmf.length;

  return (
    <div data-testid="tooth-damage">
      <div className="vai-mb-2 vai-flex vai-justify-between vai-items-center">
        <div className="vai-flex vai-items-center vai-gap-1">
          <p className="vai-font-inter vai-text-xs vai-text-zinc-300 vai-uppercase">
            <Trans>Tooth Damage</Trans>
          </p>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="vai-text-zinc-300 vai-text-xs"
                />
              </TooltipTrigger>
              <TooltipContent variant="dark" portal className="vai-max-w-xs">
                <p className="vai-text-sm [@media(max-width:310px)]:!vai-text-[12px] vai-leading-relaxed">
                  <Trans>
                    Tooth damage is the percentage of tooth structure lost from
                    decay, breaks/chips, or fillings.
                  </Trans>
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {!hideToggle && !isLoading && (
          <button
            data-testid="tooth-damage-toggle"
            className="vai-font-inter vai-text-xs vai-text-zinc-300 vai-underline"
            onClick={handleMeasurementsToggle}
          >
            {showMore ? <Trans>Show less</Trans> : <Trans>Show all</Trans>}
          </button>
        )}
      </div>
      {isLoading ? (
        <div className="vai-flex vai-flex-col vai-gap-2 [@media(max-height:570px)]:vai-gap-1">
          <Skeleton className="vai-w-full vai-h-4" />
          <Skeleton className="vai-w-full vai-h-4" />
          <Skeleton className="vai-w-full vai-h-4" />
          <Skeleton className="vai-w-full vai-h-4" />
        </div>
      ) : (
        <>
          <div className="vai-grid vai-grid-cols-[auto_auto_1fr] vai-gap-2 [@media(max-height:570px)]:vai-gap-1 vai-items-center">
            {displayTeeth?.map(({ toothId, dmf }) => {
              // Checking for truthiness instead of a number because we don't want to show
              // DMF of 0%
              if (!dmf) return null;

              return (
                <Fragment key={toothId}>
                  <FontAwesomeIcon color="white" icon={faTooth} />
                  <p className="vai-typography-small vai-text-white">
                    {displayToothNumber(toothId)}
                  </p>
                  <ToothDamageProgress
                    className="vai-bg-gradient-to-r vai-from-zinc-300 vai-to-zinc-600"
                    dmf={dmf}
                    indicatorClassName="vai-bg-gradient-to-r vai-from-[#DEC53A] vai-to-[#FF0000] vai-to-55%"
                    isMasked
                    key={toothId}
                  />
                </Fragment>
              );
            })}
          </div>
          {!hasDmfMeasurements && (
            <p className="vai-font-inter vai-text-sm vai-text-white">
              <Trans>No measurements available</Trans>
            </p>
          )}
          {showNoSignificantDmfMessage && (
            <p className="vai-font-inter vai-text-sm vai-text-white">
              <Trans>No measurements</Trans> &gt;15%
            </p>
          )}
        </>
      )}
    </div>
  );
};
